import $ from 'jquery';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel } from 'react-bootstrap';
import { ChoiceGroup, DefaultButton, Dialog, DialogFooter, DialogType, Label, PrimaryButton } from '@fluentui/react';
import { AudienceGroup } from '@bingads-webui/campaign-odata-schemas';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ErrorMessages, ValidationGroup } from '@bingads-webui-react/validation';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { withScope } from '@bingads-webui-react/scope-service';

import { AudienceGroupNegativeReferenceId } from '../constants/audience-group';
import { AssetGroupsGrid } from './asset-groups-grid';
import { getSaveDialogStyle } from '../styles/save-dialog';
import { viewModelToModelForAudienceGroup, saveAudienceGroup } from '../model/audience-group';

const SaveOption = {
  SAVE: 1,
  SAVECOPY: 2,
};

const UnstyledSaveDialog = (props) => {
  const {
    audienceGroupViewModel,
    classes,
    i18n,
    isOpen,
    onAudienceGroupChanged,
    onClose,
    onDismiss,
    onGenericError,
    onSaveInPlace,
    scope,
  } = props;

  const [viewModel, setViewModel] = useState({
    ...audienceGroupViewModel,
    data: {
      ...audienceGroupViewModel.data,
      Id: AudienceGroupNegativeReferenceId,
      Name: i18n.getString(_TL_('{{name}} - copy'), { name: audienceGroupViewModel.data.Name }),
    },
  });

  const [serverErrors, setServerErrors] = useState([]);

  const [alwaysShowError, toggleAlwaysShowError] = useState(false);

  const [saveOption, setSaveOption] = useState(null);

  const showSaveCopyFields = useMemo(() => saveOption === SaveOption.SAVECOPY, [saveOption]);

  const onValidate = useCallback((results) => {
    setViewModel({
      ...results,
      data: {
        ...viewModel.data,
        ...results.data,
      },
    });
  }, [viewModel.data]);

  const onSave = useCallback(() => {
    if (showSaveCopyFields) {
      toggleAlwaysShowError(true);
      if (!viewModel.isValid) {
        return;
      }

      saveAudienceGroup(viewModel, i18n)
        .then(response => onAudienceGroupChanged(viewModelToModelForAudienceGroup(response)))
        .then(onClose)
        .catch((response) => {
          setServerErrors(response.errors);
        })
        .finally(withUiBlock($('.ms-Dialog .ms-Dialog-main')));
    } else {
      onDismiss();
      onSaveInPlace();
    }
  }, [showSaveCopyFields, viewModel, i18n, onClose, onAudienceGroupChanged, onDismiss, onSaveInPlace]);

  const dialogContentProps = useMemo(() => ({
    className: 'audience-save-dialog',
    type: DialogType.normal,
    title: i18n.getString(_TL_('How do you want to save this audience signal?')),
  }), [i18n]);

  const onRenderSaveOption = useCallback(({ text, subText }) => (
    <div className="save-option">
      <div>{text}</div>
      <div>{subText}</div>
    </div>
  ), []);

  const onChangeSaveOption = useCallback((_event, { key }) => {
    setSaveOption(key);
  }, []);

  const saveOptions = useMemo(() => [
    {
      key: SaveOption.SAVE,
      text: i18n.getString(_TL_('Save this audience signal')),
      subText: i18n.getString(_TL_('This audience signal will be updated, and any changes will be applied to all asset groups.')),
      onRenderLabel: onRenderSaveOption,
    },
    {
      key: SaveOption.SAVECOPY,
      text: i18n.getString(_TL_('Save as a copy')),
      subText: i18n.getString(_TL_('The original audience signal will remain unchanged.')),
      onRenderLabel: onRenderSaveOption,
    },
  ], [i18n, onRenderSaveOption]);

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={dialogContentProps}
      maxWidth={1280}
    >
      <div className={classNames(['audience-signal-save-ui', classes.container])}>
        <Panel>
          <Panel.Heading>
            <Panel.Title className="audience-signal-usage" toggle>
              <ReactTemplate
                model={{
                  usage: (
                    <span className="usage">
                      {i18n.getString(
                        audienceGroupViewModel.data.AssociationsCount > 1 ?
                        _TL_('{{count}} asset groups') :
                        _TL_('{{count}} asset group'),
                        { count: audienceGroupViewModel.data.AssociationsCount }
                      )}
                    </span>
                  ),
                }}
                template={i18n.getString(_TL_('This audience signal is currently being used in {{usage}}'))}
              />
            </Panel.Title>
          </Panel.Heading>
          <Panel.Collapse>
            <Panel.Body>
              <AssetGroupsGrid
                i18n={i18n}
                cid={scope.values.cid}
                aid={scope.values.aid}
                audienceGroupId={audienceGroupViewModel.data.Id}
                onGenericError={onGenericError}
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>
        <ChoiceGroup
          className="save-options"
          options={saveOptions}
          onChange={onChangeSaveOption}
        />
        {showSaveCopyFields && (
          <ValidationGroup validationProps={{
            data: viewModel.data,
            schema: AudienceGroup,
            onValidate,
          }}
          >
            <div className="audience-signal-name">
              <Label>{i18n.getString(_TL_('New audience signal name'))}</Label>
              <RemainingCharsValidatedTextField
                className="input-audience-signal-name"
                i18n={i18n}
                placeholder={i18n.getString(_TL_('Enter a name for your audience signal'))}
                validationProps={{
                  field: 'Name',
                  schema: AudienceGroup,
                  alwaysShowError,
                }}
              />
            </div>
            {serverErrors && (
              <div className="server-errors">
                <ErrorMessages messages={serverErrors} />
              </div>
            )}
          </ValidationGroup>
        )}
      </div>
      <DialogFooter>
        <PrimaryButton className="btn-save" disabled={!saveOption} onClick={onSave}>
          {i18n.getString(_TL_('Save'))}
        </PrimaryButton>
        <DefaultButton className="btn-cancel" onClick={onDismiss}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </DialogFooter>
    </Dialog>
  );
};

UnstyledSaveDialog.propTypes = {
  audienceGroupViewModel: PropTypes.shape({
    data: PropTypes.shape({
      AssociationsCount: PropTypes.number,
      Id: PropTypes.number,
      Name: PropTypes.string,
    }),
  }).isRequired,
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool,
  onAudienceGroupChanged: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onGenericError: PropTypes.func.isRequired,
  onSaveInPlace: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.number,
      cid: PropTypes.number,
      assetGroupId: PropTypes.number,
    }),
  }).isRequired,
};

UnstyledSaveDialog.defaultProps = {
  isOpen: false,
};

export const SaveDialog = withDefaultStyles(withScope(UnstyledSaveDialog), getSaveDialogStyle);
